import { Cloud } from 'lucide-react';
import { useState } from 'react';
import type { ProvisioningRunResult } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { Button, FormField, Input, Modal } from '@/components/ui';
import { startProvisioning } from '@/data';
import { errorMessage } from '@/lib/api';

interface Props {
  businessId: string;
  businessName: string;
  defaultBranchName?: string;
  defaultBranchRef?: string;
  existingCaBranches?: Array<{ id: string; name: string; referenceCode: string }>;
  onClose: () => void;
  onDone?: () => void;
}

/**
 * Set Up Cloud POS — creates ONE Production Tenant when none exists,
 * regardless of License/Updater links. Idempotent via provisioning saga.
 */
export function StartProductionSetupModal({
  businessId,
  businessName,
  defaultBranchName,
  defaultBranchRef,
  existingCaBranches = [],
  onClose,
  onDone,
}: Props) {
  const can = useCan();
  const allowed = can('tenant.manage');
  const primary = existingCaBranches[0];
  const [branchName, setBranchName] = useState(defaultBranchName || primary?.name || 'Main Branch');
  const [branchRef, setBranchRef] = useState(defaultBranchRef || primary?.referenceCode || 'MAIN');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisioningRunResult | null>(null);
  const [copied, setCopied] = useState(false);

  if (!allowed) {
    return (
      <Modal open title="Set Up Cloud POS" onClose={onClose} size="md">
        <p className="text-sm text-ink-secondary">You need the tenant.manage permission to set up Cloud POS.</p>
      </Modal>
    );
  }

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const branches =
        existingCaBranches.length > 0
          ? existingCaBranches.map((b, i) => ({
              branchRef: b.referenceCode,
              name: b.name,
              primary: i === 0,
              localBranchId: b.id,
            }))
          : [{ branchRef: branchRef.trim().toUpperCase(), name: branchName.trim(), primary: true as const }];

      const res = await startProvisioning(businessId, { branches });
      setResult(res);
      onDone?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const enrollment = result?.secrets?.enrollmentCodes?.[0]?.enrollmentCode ?? null;
  const status = result?.provisioning.status;

  return (
    <Modal
      open
      title="Set Up Cloud POS"
      onClose={onClose}
      size="md"
      footer={
        result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={submitting} disabled={!branchName.trim() || !branchRef.trim()}>
              Create Cloud POS Account
            </Button>
          </>
        )
      }
    >
      {!result ? (
        <div className="space-y-4">
          <p className="text-sm text-ink-secondary">
            This will create the Cloud POS account for <strong className="text-ink">{businessName}</strong> and connect
            its branches to the Quds POS Cloud service. License and updater links are unchanged. If this pharmacy already
            exists in Production, use <strong className="text-ink">Production Linking</strong> instead — do not create a
            second account.
          </p>
          {existingCaBranches.length === 0 ? (
            <>
              <FormField label="First branch name" htmlFor="start-prod-branch-name" required>
                <Input id="start-prod-branch-name" value={branchName} onChange={(e) => setBranchName(e.target.value)} maxLength={200} />
              </FormField>
              <FormField label="Branch ID" htmlFor="start-prod-branch-ref" hint="Must match the Branch ID configured in the pharmacy POS." required>
                <Input
                  id="start-prod-branch-ref"
                  value={branchRef}
                  onChange={(e) => setBranchRef(e.target.value.toUpperCase())}
                  maxLength={50}
                  className="font-mono uppercase"
                />
              </FormField>
            </>
          ) : (
            <div className="rounded-md border border-line bg-surface-muted px-3 py-3 text-sm text-ink-secondary">
              Existing locations will be connected under one Cloud POS account:
              <ul className="mt-2 list-disc pl-5">
                {existingCaBranches.map((b) => (
                  <li key={b.id}>
                    {b.name} <span className="font-mono text-xs">({b.referenceCode})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{error}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-line bg-surface-muted px-3 py-3">
            <Cloud size={18} className="mt-0.5 text-primary" aria-hidden />
            <div>
              <p className="text-sm font-medium text-ink">Cloud POS setup {status === 'COMPLETED' ? 'completed' : status}</p>
              <p className="mt-1 text-[13px] text-ink-secondary">
                Next: open Branches → Generate API → copy into the Point of Sale for each location.
              </p>
            </div>
          </div>
          {enrollment && (
            <div className="rounded-md border border-warning/30 bg-warning-soft px-3 py-3">
              <p className="text-[12px] font-medium uppercase tracking-wide text-warning">Enrollment code (optional, shown once)</p>
              <p className="mt-1 font-mono text-sm text-ink">{enrollment}</p>
              <Button
                className="mt-2"
                size="sm"
                variant="secondary"
                onClick={async () => {
                  await navigator.clipboard.writeText(enrollment);
                  setCopied(true);
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          )}
          {result.provisioning.lastError && <p className="text-sm text-danger">{result.provisioning.lastError}</p>}
          <ul className="space-y-1 text-[13px] text-ink-secondary">
            {result.provisioning.steps.map((s) => (
              <li key={s.key}>
                <span className="font-mono text-ink">{s.key}</span> — {s.status}
                {s.error ? ` (${s.error})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
